/**
 * Panel API klienti.
 *
 * Sessiya httpOnly cookie-dədir — token JS-ə heç vaxt görünmür, ona görə
 * XSS onu oğurlaya bilmir. `credentials: 'same-origin'` kifayətdir.
 */

import * as telemetry from './telemetry';
import type {
  MailCategory,
  Address,
  Alias,
  BillingProfileInput,
  BillingSummary,
  CurrentProvider,
  DeliveryReport,
  DmarcCollectResult,
  DnsRecheckResult,
  DnsRecord,
  Domain,
  DomainReport,
  Folder,
  MessageDetail,
  MessageSummary,
  Mailbox,
  MigrationJob,
  MigrationProvider,
  Payment,
  SearchResult,
  SendResult,
  User,
} from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    /**
     * Cavabın tam gövdəsi.
     *
     * ⚠️ `code` çox vaxt KİFAYƏT ETMİR. Məsələn köçürmə bağlantısı
     * sınanda server `{error:"connect_failed", reason:"<insan cümləsi>"}`
     * qaytarır — əsl dəyər `reason`-dadır. Yalnız `code` saxlasaydıq,
     * interfeys istifadəçiyə «connect_failed» deyərdi.
     */
    public readonly body: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `exactOptionalPropertyTypes` altında `headers: undefined` yazmaq olmaz —
  // açar ya olmalıdır, ya da heç olmamalıdır.
  const options: RequestInit = { credentials: 'same-origin', ...init };
  if (init?.body) {
    options.headers = { 'Content-Type': 'application/json', ...init.headers };
  }

  const res = await fetch(path, options);

  if (!res.ok) {
    let code = `http_${res.status}`;
    let requestId: string | undefined;
    let payload: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as { error?: string; requestId?: string };
      payload = body as Record<string, unknown>;
      if (body.error) code = body.error;
      if (body.requestId) requestId = body.requestId;
    } catch {
      // Cavab JSON deyilsə status kodu ilə kifayətlənirik.
    }

    // 401 gözləniləndir (panel açılanda sessiya yoxlanılır) — səs-küy yaratmır.
    if (res.status !== 401) {
      telemetry.failure(`API uğursuz: ${init?.method ?? 'GET'} ${path} → ${code}`, {
        status: res.status,
        code,
        ...(requestId ? { serverRequestId: requestId } : {}),
      });
    }

    throw new ApiError(res.status, code, payload);
  }

  return (await res.json()) as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (input: {
    email: string;
    password: string;
    company: string;
    fullName?: string;
    /** Ayrıca marketinq razılığı — şərtlərlə birləşdirilməyib. */
    marketingOptIn?: boolean;
  }) =>
    request<{ user: User }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /** Təsdiq məktubunu yenidən göndərir — saatda 3 dəfə. */
  resendVerification: () =>
    request<{ ok: true; sentTo: string }>('/api/auth/resend-verification', { method: 'POST' }),

  // ── Köçürmə (docs/13-kocurme.md) ──────────────────────────────────────
  migrationProviders: () =>
    request<{ providers: MigrationProvider[] }>('/api/migration/providers'),

  migrationDetect: (email: string) =>
    request<{ provider: string | null; mx: string[]; alreadyOurs: boolean }>(
      `/api/migration/detect?email=${encodeURIComponent(email)}`,
    ),

  migrationTest: (host: string, port: number, user: string, password: string) =>
    request<{ ok: true; folders: number; messages: number }>('/api/migration/test', {
      method: 'POST',
      body: JSON.stringify({ host, port, user, password }),
    }),

  migrationJobs: () => request<{ jobs: MigrationJob[] }>('/api/migration/jobs'),

  migrationStart: (provider: string, host: string, port: number, user: string, password: string) =>
    request<{ ok: true; jobId: string }>('/api/migration/jobs', {
      method: 'POST',
      body: JSON.stringify({ provider, host, port, user, password }),
    }),

  /** Sistemin sağlamlığı — `mailora-health.timer` nəticələri. */
  systemHealth: () =>
    request<{
      status: 'ok' | 'warn' | 'error' | 'stale' | 'unknown';
      ranAt: string | null;
      stale: boolean;
      problems?: number;
      checks: { id: string; severity: string; summary: string }[];
      note?: string;
    }>('/api/system/health'),

  /** Göndəriş limiti və cari istifadə (docs/14-limitler.md). */
  sendUsage: () =>
    request<{
      hour: number;
      day: number;
      hourlyLimit: number;
      dailyLimit: number;
      pool: string;
    }>('/api/admin/send-usage'),

  // ── Çatdırılma nəzarəti ────────────────────────────────────────────────
  /*
    ⚠️ ÜÇÜ DƏ YALNIZ PLATFORMA OPERATORU ÜÇÜNDÜR (`requirePlatformAdmin`).
    Cavabda BÜTÜN kirayəçilərin domenləri var — panel bu ucları yalnız
    `user.isPlatformAdmin` olanda çağırır ki, hər müştəri panel açanda
    boş yerə rədd cavabı alıb jurnalı doldurmasın.

    ⚠️ ÜÇ UCUN DA PREFİKSİ EYNİDİR — `/api/system/delivery`. Arxa tərəfdə
    ad dəyişsə, düzəliş bu üç sətirdədir; ekran kodunda ünvan yoxdur.
  */

  /**
   * Domenlərin DNS + DKIM açarı + DMARC yekunu.
   *
   * @param days DMARC yekununun əhatə etdiyi gün sayı. Arxa tərəf onu
   *   1–400 aralığına sıxır — paneldən gələn nəhəng dəyər bütün cədvəli
   *   skan etməsin deyə.
   */
  delivery: (days = 30) =>
    request<DeliveryReport>(`/api/system/delivery?days=${days}`),

  /**
   * BÜTÜN aktiv domenlərin DNS-ini indi yenidən yoxlayır.
   *
   * ⚠️ Bu, `verifyDomain()` DEYİL: ora bir domenə aiddir və uğurlu olsa
   * domeni AKTİVLƏŞDİRİR. Bura isə nəzarətdir — mövcud vəziyyəti
   * yeniləyir və «keçirdi → keçmir» dəyişikliyini hadisə kimi yazır.
   * Fərq real nasazlıqdan çıxdı: bir domen bazada «MX qaydasındadır»
   * yazılıb 13 gün gəzdi, çünki DNS yalnız aktivləşmə anında bir dəfə
   * yoxlanılırdı.
   */
  recheckDns: () =>
    request<DnsRecheckResult>('/api/system/delivery/dns-recheck', { method: 'POST' }),

  /** DMARC hesabatlarını IMAP qutusundan yığır və bazaya yazır. */
  collectDmarc: () =>
    request<DmarcCollectResult>('/api/system/delivery/dmarc-collect', { method: 'POST' }),

  /**
   * Yarımçıq qalmış köçürməni davam etdirir.
   *
   * ⚠️ ARXA TƏRƏFDƏ ÇOXDAN VARDI (`POST /api/migration/jobs/:id/resume`),
   * amma ön tərəfdə heç vaxt çağırılmırdı — yəni uc ölü idi. Şəbəkə
   * kəsildiyi üçün dayanan köçürməni davam etdirməyin YEGANƏ yolu
   * hər şeyi sıfırdan başlatmaq idi.
   */
  migrationResume: (id: string, password: string) =>
    request<{ ok: true }>(`/api/migration/jobs/${encodeURIComponent(id)}/resume`, {
      method: 'POST',
      // ⚠️ PAROL MƏCBURİDİR (server.ts → `password_required`).
      // Onu heç yerdə saxlamırıq — nə bazada, nə sessiyada. Ona görə
      // davam etdirmə istifadəçinin parolu YENİDƏN yazmasını tələb edir.
      body: JSON.stringify({ password }),
    }),

  migrationCancel: (id: string) =>
    request<{ ok: true }>(`/api/migration/jobs/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    }),

  /**
   * Şifrə sıfırlama sorğusu.
   *
   * ⚠️ Cavab HƏMİŞƏ eynidir — ünvan qeydiyyatda olsa da, olmasa da. Bu,
   * serverin qəsdən seçimidir: fərqli cavab versəydi, bu endpoint hansı
   * ünvanların bizdə hesabı olduğunu yoxlamaq üçün alətə çevrilərdi.
   * Ona görə interfeys də «göndərdik» deyir, «tapılmadı» yox.
   */
  forgot: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ ok: true; email: string }>('/api/auth/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  verifyEmail: (token: string) =>
    request<{ ok: true; email: string }>('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /** Sistemin istifadəçiyə təsir edən vəziyyəti (xarici göndəriş işləyirmi). */
  systemStatus: () =>
    request<{ outbound: { blocked: boolean; deferred24h: number; lastAt: string | null } }>(
      '/api/system/status',
    ),

  /** Hansı xarici giriş üsulları qurulub. Düymə yalnız buna görə göstərilir. */
  providers: () => request<{ google: boolean }>('/api/auth/providers'),

  me: () => request<{ user: User }>('/api/auth/me'),

  folders: () => request<{ folders: Folder[] }>('/api/mail/folders'),

  /**
   * @param category `null` — bütün nişanlar. Yalnız INBOX-da nəzərə alınır.
   */
  messages: (path: string, offset = 0, limit = 50, category: MailCategory | null = null) =>
    request<{
      messages: MessageSummary[];
      total: number;
      /** Nişanlara görə say — YALNIZ taranmış hissədə. */
      counts: Record<MailCategory, number>;
      /** Neçə məktub oxundu. `total`-dan azdırsa, siyahı tam deyil. */
      scanned: number;
    }>(
      `/api/mail/messages?path=${encodeURIComponent(path)}&offset=${offset}&limit=${limit}` +
        (category ? `&category=${category}` : ''),
    ),

  message: (path: string, uid: number) =>
    request<{ message: MessageDetail }>(
      `/api/mail/message?path=${encodeURIComponent(path)}&uid=${uid}`,
    ),

  setFlags: (path: string, uids: number[], add: string[], remove: string[] = []) =>
    request<{ ok: true }>('/api/mail/flags', {
      method: 'POST',
      body: JSON.stringify({ path, uids, add, remove }),
    }),

  send: (input: {
    /** Alias adından göndəriş. Verilmirsə qutunun öz ünvanı işlədilir. */
    from?: string;
    to: Address[];
    cc?: Address[];
    bcc?: Address[];
    subject: string;
    text?: string;
    inReplyTo?: string;
    references?: string[];
    /** Yönləndirmə mənbəyi — qoşmaları server özü əlavə edir. */
    forwardOf?: { path: string; uid: number };
    /** `content` base64-dür — JSON ikili məlumat daşıya bilmir. */
    attachments?: { filename: string; content: string; contentType?: string }[];
  }) =>
    request<SendResult>('/api/mail/send', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  search: (path: string, q: string, limit = 50) =>
    request<SearchResult>(
      `/api/mail/search?path=${encodeURIComponent(path)}&q=${encodeURIComponent(q)}&limit=${limit}`,
    ),

  /** Qoşma endirmə ünvanı — `fetch` yox, birbaşa keçid işlədilir. */
  attachmentUrl: (path: string, uid: number, part: string): string =>
    `/api/mail/attachment?path=${encodeURIComponent(path)}&uid=${uid}&part=${encodeURIComponent(part)}`,

  createFolder: (name: string) =>
    request<{ ok: true; path: string }>('/api/mail/folders', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  renameFolder: (path: string, name: string) =>
    request<{ ok: true; path: string }>(`/api/mail/folders/${encodeURIComponent(path)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  deleteFolder: (path: string) =>
    request<{ ok: true; deleted: number }>(`/api/mail/folders/${encodeURIComponent(path)}`, {
      method: 'DELETE',
    }),

  signature: () => request<{ signature: string | null }>('/api/auth/signature'),

  setSignature: (signature: string | null) =>
    request<{ ok: true; signature: string | null }>('/api/auth/signature', {
      method: 'PUT',
      body: JSON.stringify({ signature }),
    }),

  /** Qutunun yaddaş istifadəsi. `null` — server kvota vermir. */
  quota: () =>
    request<{ quota: { usedBytes: number; limitBytes: number } | null }>('/api/mail/quota'),

  /** Qaralamanı saxlayır; `replaceUid` verilsə köhnə nüsxə silinir. */
  saveDraft: (input: {
    to?: Address[];
    cc?: Address[];
    bcc?: Address[];
    subject?: string;
    text?: string;
    inReplyTo?: string;
    references?: string[];
    replaceUid?: number;
  }) =>
    request<{ ok: true; uid: number | null; path: string }>('/api/mail/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** HƏMİŞƏLİK silir — geri dönüşü yoxdur. */
  deleteForever: (path: string, uids: number[]) =>
    request<{ ok: true; deleted: number }>('/api/mail/delete', {
      method: 'POST',
      body: JSON.stringify({ path, uids }),
    }),

  /** Səbəti və ya Spamı tamamilə boşaldır. */
  emptyFolder: (path: string) =>
    request<{ ok: true; deleted: number }>('/api/mail/empty', {
      method: 'POST',
      body: JSON.stringify({ path }),
    }),

  /** Cavabdakı `uids` — HƏDƏF qovluqdakı YENİ nömrələr (geri qaytarma üçün). */
  move: (from: string, to: string, uids: number[]) =>
    request<{ ok: true; uids: number[] }>('/api/mail/move', {
      method: 'POST',
      body: JSON.stringify({ from, to, uids }),
    }),

  // ── Domen idarəsi ───────────────────────────────────────────────────────
  // Hamısı `requireAdmin` altındadır — yalnız kirayəçi sahibi çağıra bilər.

  domains: () => request<{ domains: Domain[] }>('/api/domains'),

  correctDomain: (id: string, domain: string) =>
    request<{ id: string; domain: string; currentProvider: CurrentProvider }>(
      `/api/domains/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ domain }),
      },
    ),

  addDomain: (domain: string) =>
    // ⚠️ `currentProvider` — domendə HAZIRDA hansı poçt xidmətinin olduğu.
    // Onboarding bunun əsasında xəbərdarlıq göstərir; bax dns/provider.ts.
    request<{ id: string; domain: string; currentProvider: CurrentProvider }>('/api/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }),

  /**
   * DNS yazılarını başqa ünvana göndərir.
   *
   * Qeydiyyatdan keçən adam çox vaxt registrar parolunu tutan adam deyil —
   * bu düymə funnel-in ən böyük tıxacını açır.
   */
  emailDnsRecords: (id: string, to: string) =>
    request<{ ok: true; sentTo: string }>(
      `/api/domains/${encodeURIComponent(id)}/dns/email`,
      { method: 'POST', body: JSON.stringify({ to }) },
    ),

  // ── Ünvan ləqəbləri ───────────────────────────────────────────────────

  aliases: () => request<{ aliases: Alias[] }>('/api/aliases'),

  createAlias: (source: string, destination: string) =>
    request<{ id: string }>('/api/aliases', {
      method: 'POST',
      body: JSON.stringify({ source, destination }),
    }),

  setAliasActive: (id: string, active: boolean) =>
    request<{ ok: true }>(`/api/aliases/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  deleteAlias: (id: string) =>
    request<{ ok: true }>(`/api/aliases/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * Bu qutunun göndərə biləcəyi ünvanlar — Compose-dakı «Kimdən» siyahısı.
   * Öz ünvanı həmişə birincidir.
   */
  identities: () => request<{ addresses: string[] }>('/api/mail/identities'),

  // ── Abunə ─────────────────────────────────────────────────────────────

  billing: () => request<{ billing: BillingSummary }>('/api/billing/summary'),

  saveBillingProfile: (input: BillingProfileInput) =>
    request<{ ok: true }>('/api/billing/profile', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  requestInvoice: () =>
    request<{ ok: true }>('/api/billing/request-invoice', { method: 'POST' }),

  /**
   * Kartla ödənişi BAŞLADIR — abunəni açmır.
   *
   * ⚠️ MƏBLƏĞ GÖNDƏRİLMİR, YALNIZ AY SAYI. Qiyməti server
   * `billingSummary().totalMinor`-dan özü oxuyur. Məbləği bura qoysaydıq,
   * brauzerin konsolundan «amount: 1» göndərib 5 AZN-lik abunəni bir
   * qəpiyə almaq olardı — ödəniş axınında ən klassik səhv budur.
   *
   * Cavabdakı `url` şlüzün öz səhifəsidir; ön tərəfin yeganə işi
   * istifadəçini oraya atmaqdır. Kart məlumatı bizim domenə heç vaxt
   * yazılmır.
   */
  startPayment: (months?: number, method: 'card' | 'wallet' = 'card') =>
    request<{ url: string }>('/api/billing/pay', {
      method: 'POST',
      body: JSON.stringify({ months: months ?? 1, method }),
    }),

  /**
   * Son ödənişlər — «pul getdi, abunə hanı?» sualına baxılan yer.
   *
   * ⚠️ `billing()` YERİNƏ KEÇMİR: o, yalnız CARİ vəziyyəti (paid_until,
   * status) verir və geriyə doğru heç nə göstərmir. Tarixçə olmasa,
   * dəstək müştəriyə «bilmirəm» deyir və söhbət birbaşa banka gedir.
   */
  payments: () => request<{ payments: Payment[] }>('/api/billing/payments'),

  domainDns: (id: string) =>
    request<{ domain: string; records: DnsRecord[] }>(
      `/api/domains/${encodeURIComponent(id)}/dns`,
    ),

  /** DNS-i yoxlayır və hər şey qaydasındadırsa domeni aktivləşdirir. */
  verifyDomain: (id: string) =>
    request<{ report: DomainReport; activated: boolean }>(
      `/api/domains/${encodeURIComponent(id)}/verify`,
      { method: 'POST' },
    ),

  mailboxes: () => request<{ mailboxes: Mailbox[] }>('/api/mailboxes'),

  updateMailbox: (
    id: string,
    patch: { displayName?: string | null; password?: string; active?: boolean },
  ) =>
    request<{ ok: true }>(`/api/mailboxes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteMailbox: (id: string) =>
    request<{ ok: true; email: string }>(`/api/mailboxes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // ── Öz profili ──────────────────────────────────────────────────────────

  updateProfile: (displayName: string | null) =>
    request<{ ok: true; displayName: string | null }>('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),

  /** Uğurlu olsa sessiya ləğv olunur — istifadəçi yenidən girməlidir. */
  changePassword: (current: string | null, next: string) =>
    request<{ ok: true; reauth: boolean }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ ...(current ? { current } : {}), next }),
    }),

  createMailbox: (input: {
    domainId: string;
    localPart: string;
    password: string;
    displayName?: string;
    quotaGb?: number;
  }) =>
    request<{ email: string }>('/api/mailboxes', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
